import {
  Fragment,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from "react";
import { useShallow } from "zustand/react/shallow";
import { Button } from "@/components/ui";
import { MessageAttachmentStrip } from "@/components/panels/message-attachment-strip";
import { Icons } from "@/components/ui/icons";
import {
  MarkdownContent,
  type MarkdownLinkClickHandler,
} from "@/components/ui/markdown-content";
import { useT } from "@/i18n";
import { framesConversationMessages } from "@/i18n/messages/frames-conversation";
import { framesSharedMessages } from "@/i18n/messages/frames-shared";
import { cn } from "@/lib/cn";
import { copyToClipboard } from "@/lib/copy-to-clipboard";
import { useFramesStore } from "@/stores/frames-store";
import { selectOrderedFrames } from "@/stores/frames-selectors";
import { useConversationCompacting } from "@/stores/conversation-compaction-store";
import type { ConversationMessage } from "@/stores/conversation-store";
import { ConversationExecutionSteps } from "./conversation-execution-steps";
import { ConversationDirectApprovalCard } from "./conversation-direct-approval-card";
import { ConversationDirectResultNotice } from "./conversation-direct-result-notice";
import { ConversationFilingNotice } from "./conversation-filing-notice";
import { ConversationNativeResultNotice } from "./conversation-native-result-notice";
import {
  ConversationCompactingNote,
  ConversationSystemMessage,
} from "./conversation-system-message";
import { ConversationFrameEmbed } from "./conversation-frame-embed";
import { resolveArtifactsForExecutionIds } from "./conversation-artifact-resolver";
import { answerItemByRun, buildThreadItems } from "./conversation-thread-items";
import { formatMessageTime } from "./conversation-frame-utils";

interface ConversationFrameThreadProps {
  messages: readonly ConversationMessage[];
  scrollRef: RefObject<HTMLDivElement | null>;
  onScroll: () => void;
  showLoadingPlaceholder: boolean;
  isRunning: boolean;
  newChatPending?: boolean;
  runningExecutionIds: ReadonlySet<string>;
  onLinkClick: MarkdownLinkClickHandler;
  onSuggestionSend?: (text: string) => void;
  suggestionProjectId?: string;
  conversationFrameId?: string;
  testId?: string;
}

export const ConversationFrameThread = memo(function ConversationFrameThread({
  messages,
  scrollRef,
  onScroll,
  showLoadingPlaceholder,
  isRunning,
  newChatPending = false,
  runningExecutionIds,
  onLinkClick,
  onSuggestionSend,
  suggestionProjectId,
  conversationFrameId,
  testId = "conversation-frame",
}: ConversationFrameThreadProps) {
  const items = useMemo(
    () =>
      newChatPending ? [] : buildThreadItems(messages, runningExecutionIds),
    [messages, newChatPending, runningExecutionIds],
  );
  const answerByRun = useMemo(() => answerItemByRun(items), [items]);
  const executionIds = useMemo(() => [...answerByRun.keys()], [answerByRun]);
  // Only this thread's artifact frames: other frames changing leave it alone.
  const artifacts = useFramesStore(
    useShallow((state) =>
      resolveArtifactsForExecutionIds(executionIds, selectOrderedFrames(state)),
    ),
  );
  const embedsByExecution = useMemo(() => {
    const map = new Map<string, typeof artifacts>();
    for (const executionId of executionIds) {
      map.set(
        executionId,
        resolveArtifactsForExecutionIds([executionId], artifacts),
      );
    }
    return map;
  }, [executionIds, artifacts]);
  const hasActiveSteps = items.some(
    (item) => item.kind === "steps" && item.active,
  );
  let conversationId: string | undefined;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    conversationId = messages[index]?.conversationId;
    if (conversationId) break;
  }
  const compacting =
    useConversationCompacting(conversationId) && !newChatPending;
  let lastConversationMessage: ConversationMessage | undefined;
  for (
    let index = newChatPending ? -1 : messages.length - 1;
    index >= 0;
    index -= 1
  ) {
    const message = messages[index];
    // A committed sentence is the agent's words too: no "Thinking" under it.
    if (
      message &&
      (!message.activityKind || message.activityKind === "progress")
    ) {
      lastConversationMessage = message;
      break;
    }
  }
  const showThinkingFallback =
    isRunning &&
    !compacting &&
    !hasActiveSteps &&
    lastConversationMessage?.role !== "assistant" &&
    lastConversationMessage?.activityKind !== "progress";

  return (
    <div
      ref={scrollRef}
      onScroll={onScroll}
      className="scrollbar-none nodrag nowheel flex min-h-0 min-w-0 flex-1 flex-col gap-2.5 overflow-y-auto px-4 pt-3.5"
      style={
        {
          paddingBottom: "var(--conversation-composer-clearance, 6rem)",
        } as CSSProperties
      }
      data-frame-content="true"
      data-native-glass-static-scroll="true"
      data-testid={`${testId}-thread`}
      role="log"
      aria-live="polite"
    >
      {items.length === 0 && !showThinkingFallback && !newChatPending ? (
        <ConversationFrameEmptyState
          showLoadingPlaceholder={showLoadingPlaceholder}
        />
      ) : (
        <>
          {items.map((item, index) =>
            item.kind === "steps" ? (
              <ConversationExecutionSteps
                key={`steps-${item.executionId}`}
                executionId={item.executionId}
                steps={item.steps}
                active={item.active}
                startedAt={item.startedAt}
                completedAt={item.completedAt}
                onLinkClick={onLinkClick}
                testId={testId}
              />
            ) : item.kind === "filing" ? (
              <ConversationFilingNotice
                key={`filing-${item.id}`}
                filing={item}
                testId={testId}
              />
            ) : (
              <ConversationThreadMessageWithEmbeds
                key={item.message.id}
                message={item.message}
                isLastInExecutionGroup={
                  !item.message.executionId ||
                  answerByRun.get(item.message.executionId) === index
                }
                embeds={
                  item.message.executionId &&
                  answerByRun.get(item.message.executionId) === index
                    ? (embedsByExecution.get(item.message.executionId) ?? [])
                    : []
                }
                conversationFrameId={conversationFrameId}
                onLinkClick={onLinkClick}
                onSuggestionSend={onSuggestionSend}
                suggestionProjectId={suggestionProjectId}
                testId={testId}
              />
            ),
          )}
          {compacting ? <ConversationCompactingNote testId={testId} /> : null}
          {showThinkingFallback ? (
            <ConversationThinkingFallback testId={testId} />
          ) : null}
        </>
      )}
      {newChatPending ? <NewChatBoundary testId={testId} /> : null}
    </div>
  );
});

function NewChatBoundary({ testId }: { testId: string }) {
  const t = useT(framesSharedMessages);
  return (
    <div
      className="flex items-center gap-2 py-2 text-canvas-xs font-medium text-secondary"
      data-testid={`${testId}-new-chat-boundary`}
      role="separator"
      aria-label={t("newChat")}
    >
      <span className="h-px flex-1 bg-glass-stroke-subtle" aria-hidden="true" />
      <span>{t("newChat")}</span>
      <span className="h-px flex-1 bg-glass-stroke-subtle" aria-hidden="true" />
    </div>
  );
}

function ConversationThinkingFallback({ testId }: { testId: string }) {
  const t = useT(framesConversationMessages);
  return (
    <div
      className="flex items-center gap-2 px-2.5 py-1.5 text-canvas-xs text-muted"
      data-testid={`${testId}-thinking`}
    >
      <Icons.Spinner
        className="h-3 w-3 shrink-0 animate-spin"
        aria-hidden="true"
      />
      <span>{t("thinking")}</span>
    </div>
  );
}

function ConversationFrameEmptyState({
  showLoadingPlaceholder,
}: {
  showLoadingPlaceholder: boolean;
}) {
  const t = useT(framesConversationMessages);
  if (!showLoadingPlaceholder) {
    return (
      <p className="px-2 py-8 text-center text-canvas-base text-muted">
        {t("emptyThread")}
      </p>
    );
  }
  return (
    <div
      className="flex flex-col items-center justify-center gap-2 px-2 py-8 text-center text-canvas-base text-muted"
      data-testid="conversation-frame-loading"
    >
      <Icons.Spinner className="h-4 w-4 animate-spin" aria-hidden="true" />
      <span>{t("loadingConversation")}</span>
    </div>
  );
}

function ConversationThreadMessageWithEmbeds({
  message,
  isLastInExecutionGroup,
  embeds,
  conversationFrameId,
  onLinkClick,
  onSuggestionSend,
  suggestionProjectId,
  testId,
}: {
  message: ConversationMessage;
  isLastInExecutionGroup: boolean;
  embeds: readonly { frameId: string }[];
  conversationFrameId?: string;
  onLinkClick: MarkdownLinkClickHandler;
  onSuggestionSend?: (text: string) => void;
  suggestionProjectId?: string;
  testId: string;
}) {
  return (
    <Fragment>
      <ConversationFrameMessage
        message={message}
        onLinkClick={onLinkClick}
        onSuggestionSend={onSuggestionSend}
        suggestionProjectId={suggestionProjectId}
        testId={testId}
      />
      {isLastInExecutionGroup && embeds.length > 0 ? (
        <div
          className="flex max-w-[92%] flex-col gap-1.5"
          data-testid={`${testId}-embeds-${message.executionId}`}
        >
          {embeds.map((artifact) => (
            <ConversationFrameEmbed
              key={artifact.frameId}
              frameId={artifact.frameId}
              conversationFrameId={conversationFrameId}
            />
          ))}
        </div>
      ) : null}
    </Fragment>
  );
}

function ConversationFrameMessage({
  message,
  onLinkClick,
  onSuggestionSend,
  suggestionProjectId,
  testId,
}: {
  message: ConversationMessage;
  onLinkClick: MarkdownLinkClickHandler;
  onSuggestionSend?: (text: string) => void;
  suggestionProjectId?: string;
  testId: string;
}) {
  const t = useT(framesConversationMessages);
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    },
    [],
  );

  if (message.role === "system" && message.backgroundResult) {
    return (
      <ConversationNativeResultNotice
        id={message.id}
        title={message.backgroundResult.title}
        taskId={message.backgroundResult.taskId}
        content={message.content}
        testId={testId}
      />
    );
  }
  if (message.role === "system") {
    return (
      <ConversationSystemMessage
        id={message.id}
        content={message.content}
        testId={testId}
      />
    );
  }
  if (message.directApproval) {
    return (
      <ConversationDirectApprovalCard
        id={message.id}
        approval={message.directApproval}
        testId={testId}
      />
    );
  }
  if (message.directResult) {
    return (
      <ConversationDirectResultNotice
        id={message.id}
        title={message.directResult.title}
        taskId={message.directResult.taskId}
        projectId={message.directResult.projectId ?? suggestionProjectId}
        content={message.content}
        suggestions={message.directResult.suggestions}
        onSuggestionSend={onSuggestionSend}
        testId={testId}
      />
    );
  }
  const isUser = message.role === "user";
  const handleCopy = async () => {
    if (!(await copyToClipboard(message.content))) return;
    setCopied(true);
    if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = setTimeout(() => setCopied(false), 1_200);
  };
  return (
    <div
      className={cn("group/message flex flex-col gap-0.5", {
        "items-end": isUser,
        "items-start": !isUser,
      })}
      data-testid={`${testId}-message-${message.id}`}
    >
      <MessageAttachmentStrip attachments={message.attachments} />
      {isUser ? (
        <div className="max-w-[84%] whitespace-pre-wrap rounded-preview px-3 py-2 text-canvas-base leading-relaxed text-primary">
          {message.content}
        </div>
      ) : (
        <MarkdownContent
          content={message.content}
          onLinkClick={onLinkClick}
          className="max-w-[92%] text-canvas-base text-secondary [&_p]:leading-relaxed"
        />
      )}
      <div
        className={cn(
          "flex items-center gap-0.5 px-1",
          isUser && "flex-row-reverse",
        )}
      >
        <span className="text-canvas-xs text-muted">
          {formatMessageTime(message.createdAt)}
        </span>
        {isUser && message.steeredIntoExecutionId ? (
          <span
            className="px-1 text-canvas-xs text-muted"
            data-testid={`${testId}-message-${message.id}-steered`}
          >
            {t("addedToTurn")}
          </span>
        ) : null}
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          onClick={() => void handleCopy()}
          aria-label={isUser ? t("copyYourMessage") : t("copyAssistantMessage")}
          className="pointer-events-none h-5 w-5 rounded-full opacity-0 transition-opacity group-hover/message:pointer-events-auto group-hover/message:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100"
          data-testid={`${testId}-message-${message.id}-copy`}
        >
          {copied ? (
            <Icons.Check className="h-3 w-3 text-success" />
          ) : (
            <Icons.Copy className="h-3 w-3" />
          )}
        </Button>
      </div>
    </div>
  );
}
